// Phase C.4a — pure participant-slot validation/mapping helpers for the
// Game half of Add/Edit Event (components/teams/GameFormDialogs.js).
// Extracted so the core "known Team ID vs known Team ID -> exactly one
// team_matchups row; known Team ID vs free-text Outside Program -> one
// team_event" mapping rule is unit-testable without a brittle React
// interaction test — see gameParticipants.test.js.
//
// A "slot" is { kind: 'team', teamId, name } (a real Division Team —
// `teamId` is always an actual Team ID picked from a real Team row,
// NEVER free text and NEVER inferred by matching `name` against a
// Team's own team_name) or { kind: 'external', name } (an Outside
// Program opponent, still a plain name this pass — a reusable Opponent
// directory with its own ID is a separate, not-yet-built concept; see
// this pass's own architecture report).

/** Null when both slots represent a valid, submittable pairing;
 * otherwise a user-facing error string. Mirrors the "VALID PARTICIPANT
 * COMBINATIONS" rule: both slots must actually be filled in, the two
 * sides can never be the same Team, and at least one side must be a
 * known Division Team (an Outside-Program-vs-Outside-Program game has
 * no Division Team in it at all, which the Division Hub has no reason
 * to schedule). */
export function validateGameParticipants(homeSlot, awaySlot) {
  const homeFilled = homeSlot && (homeSlot.kind === 'team' ? homeSlot.teamId : (homeSlot.name || '').trim());
  const awayFilled = awaySlot && (awaySlot.kind === 'team' ? awaySlot.teamId : (awaySlot.name || '').trim());
  if (!homeFilled || !awayFilled) return 'Choose both the Home Team and Away Team.';
  if (homeSlot.kind === 'external' && awaySlot.kind === 'external') return 'At least one participant must be a Team in this Division.';
  if (homeSlot.kind === 'team' && awaySlot.kind === 'team' && homeSlot.teamId === awaySlot.teamId) return 'Home Team and Away Team must be different.';
  return null;
}

/** Maps a validated Home/Away pairing onto the exact write this app's
 * two-table model requires — the ONLY place that decision is made.
 * Two known Teams -> `kind: 'internal'` (a single team_matchups row,
 * real Team IDs on both sides, never a name-matched relationship). A
 * known Team plus an Outside Program opponent -> `kind: 'external'`
 * (a team_event owned by the in-house Team's own real Team ID; the
 * opponent stays a free-text name — temporary, see the Opponent
 * Directory architecture report). Caller must validate first —
 * this assumes both slots are already filled and not both external. */
export function buildGameParticipantPayload({ homeSlot, awaySlot, neutralSite }) {
  if (homeSlot.kind === 'team' && awaySlot.kind === 'team') {
    return {
      kind: 'internal',
      teamId: homeSlot.teamId,
      opponentTeamId: awaySlot.teamId,
      homeAway: neutralSite ? 'neutral' : 'home',
    };
  }
  const teamSlot = homeSlot.kind === 'team' ? homeSlot : awaySlot;
  const externalSlot = homeSlot.kind === 'team' ? awaySlot : homeSlot;
  const opponentName = externalSlot.name.trim();
  return {
    kind: 'external',
    teamId: teamSlot.teamId,
    opponentName,
    homeAway: neutralSite ? 'neutral' : (homeSlot.kind === 'team' ? 'home' : 'away'),
    // A Game's title is never displayed anywhere (every read path shows
    // "OwnerTeam vs Opponent" instead) — auto-derived so the coach is
    // never asked to type it, one less required field on a phone.
    title: `${teamSlot.name} vs ${opponentName}`,
  };
}

/** The full Home/Away participant pool for a scheduling context. A
 * multi-Team Division Hub already hands over every Team in the
 * Division (`teamOptions.length > 1`) — that alone is the pool. A
 * single-Team context (TeamSchedulePage, or a one-Team travel Division)
 * merges in that Team's own fetched Division siblings; when there are
 * none, the pool is just that one Team, and — combined with each
 * ParticipantSlot excluding whichever Team the OTHER slot already
 * holds — the second slot naturally has zero real Team choices left,
 * collapsing straight to "+ Outside Program" with no useless sibling
 * dropdown ever rendered. */
export function computeTeamPool(teamOptions, siblingTeams) {
  if (Array.isArray(teamOptions) && teamOptions.length > 1) return teamOptions;
  return [...(teamOptions || []), ...(siblingTeams || [])];
}

/** Phase C.5c — Neutral Site UX. Home/Away is a participant designation;
 * physical venue is a separate question, and this app doesn't build
 * reusable Location storage this phase — so the rule is simply: a
 * neutral-site game must have EITHER a real typed Location OR an
 * explicit "TBD" acknowledgment, never left silently ambiguous.
 * Irrelevant for an internal Team-vs-Team matchup, which has no
 * location field at all (team_matchups stores no location), and for a
 * non-neutral game, whose Location has always been optional. Returns
 * null when valid, otherwise a user-facing error string. */
export function validateNeutralSiteLocation({ isInternalGame, neutralSite, locationName, locationTBD }) {
  if (isInternalGame || !neutralSite || locationTBD) return null;
  if ((locationName || '').trim()) return null;
  return 'Enter a Location for this Neutral Site game, or mark it TBD.';
}

/** update_team_matchup()'s own contract: participants can never change
 * on edit, only date/time/site — so editing only ever needs to know
 * which side (home/away/neutral) the fixed, already-known ownerTeamId
 * currently sits in after any Home/Away swap. */
export function resolveMatchupEditHomeAway({ ownerTeamId, homeSlot, neutralSite }) {
  if (neutralSite) return 'neutral';
  return homeSlot.teamId === ownerTeamId ? 'home' : 'away';
}
